import { getApiInstance, initializeApi } from './api';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { SyncLog, SyncLogType, Appointment4D, Procedure4D } from '../types';
import { generateUUID } from '../utils/uuid';

// Helper function to safely convert external IDs to strings
function safeExternalId(id: number | string | null): string {
  if (id === null) return '';
  return id.toString();
}

// Helper function to safely convert dates to ISO strings
function safeToISOString(dateString: string | null | undefined): string | null {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null;
  return date.toISOString();
}

async function logSync(
  userId: string,
  type: SyncLog['type'],
  direction: SyncLog['direction'],
  status: SyncLog['status'],
  details?: Record<string, any>,
  error?: string
) {
  const { data, error: logError } = await supabase
    .from('sync_logs')
    .insert({
      user_id: userId,
      type,
      direction,
      status,
      details,
      error,
      start_time: new Date().toISOString(),
      end_time: new Date().toISOString(),
    })
    .select()
    .single();

  if (logError) {
    console.error('Failed to log sync:', logError);
  }

  return data;
}

async function syncInitialData(
  userId: string,
  onProgress?: (step: string, progress: number) => void,
  onComplete?: (summary: Record<string, any>) => void,
  options?: {
    fullHistoricalSync?: boolean;
  }
) {
  // Get user's 4D credentials first
  const { data: integration, error: integrationError } = await supabase
    .from('integrations')
    .select('*')
    .eq('type', '4d')
    .eq('user_id', userId)
    .single();

  if (integrationError || !integration?.config) {
    throw new Error('4D credentials not found');
  }

  // Initialize API with credentials
  initializeApi({
    clientId: integration.config.clientId,
    clientSecret: integration.config.clientSecret,
    subscriptionKey: integration.config.subscriptionKey,
    endpointUrl: integration.config.endpointUrl || 'https://api.4d-emr.com/api/public',
  });

  const api = getApiInstance();

  // Initialize counters at the top level
  let totalCount = 0;
  let successCount = 0;
  let errorCount = 0;

  // Helper function to clear sync toast
  const clearSyncToast = () => {
    toast.dismiss('sync');
  };

  // Helper function to clear all sync toasts
  const clearAllSyncToasts = () => {
    toast.dismiss('sync');
    toast.dismiss('sync-patients');
  };

  // Helper function to safely handle API responses
  const safelyHandleResponse = <T extends Record<string, unknown>>(response: T | null, entityName: string): T[] => {
    if (!response) {
      console.warn(`No ${entityName} data received`);
      return [];
    }
    if ('Items' in response && Array.isArray(response.Items)) {
      return response.Items;
    }
    if (Array.isArray(response)) {
      return response;
    }
    if (response === null) {
      console.warn(`No ${entityName} records found`);
      return [];
    }
    console.warn(`Invalid ${entityName} response format`);
    return [];
  };

  const createSyncLog = async (
    type: SyncLog['type'],
    startTime: string
  ) => {
    const { data, error } = await supabase
      .from('sync_logs')
      .insert({
        user_id: userId,
        type,
        direction: 'inbound',
        status: 'success',
        start_time: startTime,
        end_time: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) {
      console.error('Failed to create sync log:', error);
    }

    return data;
  };

  const updateSyncLog = async (
    logId: string,
    status: SyncLog['status'],
    details?: Record<string, any>,
    error?: string
  ) => {
    const { error: updateError } = await supabase
      .from('sync_logs')
      .update({
        status,
        details,
        error,
        end_time: new Date().toISOString(),
      })
      .eq('id', logId);

    if (updateError) {
      console.error('Failed to update sync log:', updateError);
    }
  };

  const updateIntegrationLastSync = async () => {
    const { error } = await supabase
      .from('integrations')
      .update({ last_sync: new Date().toISOString() })
      .eq('user_id', userId)
      .eq('type', '4d');

    if (error) {
      console.error('Failed to update last sync time:', error);
    }
  };

  // Consolidated helper to avoid 400/409 conflicts on sync_cursors by using update→insert fallback
  const upsertSyncCursor = async (values: { cursor_skip?: number; total_expected?: number | null }) => {
    try {
      // Use native upsert with onConflict to avoid race conditions
      const { error: upsertError } = await supabase
        .from('sync_cursors')
        .upsert(
          {
            user_id: userId,
            type: 'patients',
            ...values,
            last_updated: new Date().toISOString()
          },
          {
            onConflict: 'user_id,type'
          }
        );

      if (upsertError) {
        console.warn('sync_cursors upsert warning:', upsertError);
      }
    } catch (e) {
      console.warn('sync_cursors write warning:', e);
    }
  };

  // Progress helper to interpolate within a phase
  const updatePhaseProgress = (
    label: string,
    phaseStartPercent: number,
    phaseEndPercent: number,
    currentIndex: number,
    totalItems: number,
  ) => {
    if (!onProgress || totalItems <= 0) return;
    const clampedIndex = Math.min(Math.max(currentIndex + 1, 1), totalItems);
    const span = Math.max(phaseEndPercent - phaseStartPercent, 1);
    const percent = phaseStartPercent + Math.floor((clampedIndex / totalItems) * (span - 1));
    onProgress(`${label} (${clampedIndex}/${totalItems})`, percent);
  };

  // Helper function to map 4D appointment status to enum
  // 4D Status codes: Unconfirmed=1, LeftMessage=2, Confirmed=3, Arrived=4, Roomed=5,
  // Seen=6, CheckedOut=7, LeftUnseen=8, NoShow=9, Happening=10,
  // RescheduledByOffice=11, RescheduledByPatient=12,
  // CancelledByOffice=13, CancelledByPatient=14, RunningLate=17
  const mapAppointmentStatus = (status: number): string => {
    switch (status) {
      case 1: return 'unconfirmed'; // Unconfirmed
      case 2: return 'left_message'; // LeftMessage
      case 3: return 'confirmed'; // Confirmed
      case 4: return 'arrived'; // Arrived
      case 5: return 'roomed'; // Roomed
      case 6: return 'seen'; // Seen
      case 7: return 'checked_out'; // CheckedOut
      case 8: return 'left_unseen'; // LeftUnseen
      case 9: return 'no_show'; // NoShow
      case 10: return 'happening'; // Happening
      case 11: return 'rescheduled_by_office'; // RescheduledByOffice
      case 12: return 'rescheduled_by_patient'; // RescheduledByPatient
      case 13: return 'cancelled_by_office'; // CancelledByOffice
      case 14: return 'cancelled_by_patient'; // CancelledByPatient
      case 17: return 'running_late'; // RunningLate
      default: return 'unconfirmed';
    }
  };

  try {
    if (onProgress) onProgress('Starting initial data sync...', 0);
    toast.loading('Starting initial data sync...', { id: 'sync' });

    // Ensure we have an authenticated session
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) {
      throw new Error('User not authenticated');
    }

    // Start with providers
    if (onProgress) onProgress('Syncing providers...', 10);
    toast.loading('Syncing providers...', { id: 'sync' });
    let providers;
    try {
      providers = await api.getProviders();
      if (!Array.isArray(providers)) {
        throw new Error('Invalid providers response format');
      }
    } catch (error) {
      console.error('Error fetching providers:', error);
      throw new Error('Failed to fetch providers from 4D EMR');
    }

    // Preload existing providers for skip-fast
    const existingProvidersMap = new Map<number, { id: string; external_updated_at: string | null }>();
    try {
      const { data: existingProviders } = await supabase
        .from('providers')
        .select('id, external_id, external_updated_at')
        .eq('user_id', userId);
      (existingProviders || []).forEach((p: any) => existingProvidersMap.set(p.external_id as number, { id: p.id as string, external_updated_at: p.external_updated_at as (string | null) }));
    } catch {}

    for (const provider of providers) {
      try {
        const createdDate = safeToISOString(provider.CreatedDate) || new Date().toISOString();
        const modifiedDate = safeToISOString(provider.ModifiedDate) || createdDate;

        // Skip-fast if unchanged
        const fastProv = existingProvidersMap.get(provider.ProviderId);
        if (fastProv && fastProv.external_updated_at && modifiedDate && fastProv.external_updated_at === modifiedDate) {
          continue;
        }

        // First check if provider exists
        const { data: existingProvider } = await supabase
          .from('providers')
          .select('id')
          .eq('user_id', userId)
          .eq('external_id', provider.ProviderId)
          .maybeSingle();

        if (existingProvider) {
          // Update existing provider
          await supabase
            .from('providers')
            .update({
              first_name: provider.FirstName,
              last_name: provider.LastName,
              degree: provider.Degree,
              is_active: provider.IsActive,
              external_created_at: createdDate,
              external_updated_at: modifiedDate,
              sync_status: 'synced'
            })
            .eq('id', existingProvider.id);
        } else {
          // Insert new provider
          await supabase
            .from('providers')
            .insert({
              user_id: userId,
              external_id: provider.ProviderId,
              first_name: provider.FirstName,
              last_name: provider.LastName,
              degree: provider.Degree,
              is_active: provider.IsActive,
              external_created_at: createdDate,
              external_updated_at: modifiedDate,
              sync_status: 'synced'
            });
        }
      } catch (error) {
        console.error('Error syncing provider:', error);
        continue; // Continue with next provider even if one fails
      }
    }

    // Sync staff
    if (onProgress) onProgress('Syncing staff...', 25);
    toast.loading('Syncing staff...', { id: 'sync' });
    try {
      const staffMembers = await api.getStaff();
      if (!Array.isArray(staffMembers)) {
        throw new Error('Invalid staff response format');
      }

      // Preload existing staff for skip-fast
      const existingStaffMap = new Map<number, { id: string; external_updated_at: string | null }>();
      try {
        const { data: existingStaffRows } = await supabase
          .from('staff')
          .select('id, external_id, external_updated_at')
          .eq('user_id', userId);
        (existingStaffRows || []).forEach((s: any) => existingStaffMap.set(s.external_id as number, { id: s.id as string, external_updated_at: s.external_updated_at as (string | null) }));
      } catch {}

      for (const staff of staffMembers) {
        try {
          // First check if staff exists
          const { data: existingStaff } = await supabase
            .from('staff')
            .select('id')
            .eq('user_id', userId)
            .eq('external_id', staff.Id)
            .maybeSingle();

          let staffRecord;
          const staffModifiedDate = safeToISOString(staff.ModifiedDate) || safeToISOString(staff.CreatedDate) || new Date().toISOString();
          const fast = existingStaffMap.get(staff.Id);
          if (fast && fast.external_updated_at && staffModifiedDate && fast.external_updated_at === staffModifiedDate) {
            // Up-to-date; skip details and services (but keep record.id for services preloading)
            staffRecord = { id: fast.id } as any;
          } else if (existingStaff) {
            // Update existing staff
            const { data: updatedStaff } = await supabase
              .from('staff')
              .update({
                first_name: staff.FirstName,
                last_name: staff.LastName,
                display_name: staff.NameForPatientView,
                is_active: staff.IsActive,
                external_created_at: safeToISOString(staff.CreatedDate),
                external_updated_at: staffModifiedDate,
                sync_status: 'synced'
              })
              .eq('id', existingStaff.id)
              .select()
              .single();
            staffRecord = updatedStaff;
          } else {
            // Insert new staff
            const { data: newStaff } = await supabase
              .from('staff')
              .insert({
                user_id: userId,
                external_id: staff.Id,
                first_name: staff.FirstName,
                last_name: staff.LastName,
                display_name: staff.NameForPatientView,
                is_active: staff.IsActive,
                external_created_at: safeToISOString(staff.CreatedDate),
                external_updated_at: staffModifiedDate,
                sync_status: 'synced'
              })
              .select()
              .single();
            staffRecord = newStaff;
          }

          if (!staffRecord) {
            console.error(`Failed to sync staff member: ${staff.FirstName} ${staff.LastName}`);
            continue;
          }

          // Sync staff services
          toast.loading(`Syncing services for ${staff.FirstName}...`, { id: 'sync' });
          const servicesResponse = await api.getStaffServices(staff.Id);

          // Handle empty array response for staff services
          if (!Array.isArray(servicesResponse)) {
            console.warn(`No services found for staff member: ${staff.FirstName} ${staff.LastName}`);
            continue;
          }

          // Preload existing services for this staff for skip-fast
          let existingServicesMap = new Map<number, string | null>();
          try {
            const { data: existingServices } = await supabase
              .from('staff_services')
              .select('external_id, external_updated_at')
              .eq('user_id', userId)
              .eq('staff_id', staffRecord.id);
            (existingServices || []).forEach((sv: any) => existingServicesMap.set(sv.external_id as number, sv.external_updated_at as (string | null)));
          } catch {}

          for (const service of servicesResponse) {
            try {
              const serviceCreatedDate = safeToISOString(service.CreatedDate) || new Date().toISOString();
              const serviceModifiedDate = safeToISOString(service.ModifiedDate) || serviceCreatedDate;
              const existingServiceUpdatedAt = existingServicesMap.get(service.Id);
              if (existingServiceUpdatedAt && serviceModifiedDate && existingServiceUpdatedAt === serviceModifiedDate) {
                // Up-to-date; skip
                continue;
              }
              // Check if service exists
              const { data: existingService } = await supabase
                .from('staff_services')
                .select('id')
                .eq('user_id', userId)
                .eq('staff_id', staffRecord.id)
                .eq('external_id', service.Id)
                .maybeSingle();

              if (existingService) {
                // Update existing service
                await supabase
                  .from('staff_services')
                  .update({
                    name: service.Name || 'Unnamed Service',
                    description: service.Description,
                    is_active: service.IsActive,
                    external_created_at: serviceCreatedDate,
                    external_updated_at: serviceModifiedDate,
                    sync_status: 'synced'
                  })
                  .eq('id', existingService.id);
              } else {
                // Insert new service
                await supabase
                  .from('staff_services')
                  .insert({
                    user_id: userId,
                    staff_id: staffRecord.id,
                    external_id: service.Id,
                    name: service.Name || 'Unnamed Service',
                    description: service.Description,
                    is_active: service.IsActive,
                    external_created_at: serviceCreatedDate,
                    external_updated_at: serviceModifiedDate,
                    sync_status: 'synced'
                  });
              }
            } catch (error) {
              console.error(`Error syncing service for ${staff.FirstName}:`, error);
              continue;
            }
          }
        } catch (error) {
          console.error(`Error syncing staff member ${staff.FirstName}:`, error);
          continue;
        }
      }
    } catch (error) {
      console.error('Error syncing staff:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to sync staff data';
      toast.error(errorMessage, { id: 'sync' });
      throw new Error('Failed to sync staff data: ' + errorMessage);
    }

    // Sync appointment types
    if (onProgress) onProgress('Syncing appointment types...', 40);
    toast.loading('Syncing appointment types...', { id: 'sync' });
    try {
      let appointmentTypes;
      try {
        appointmentTypes = await api.getAppointmentTypes();
      } catch (error) {
        console.error('Error fetching appointment types:', error);
        throw new Error('Failed to fetch appointment types from 4D EMR');
      }

      for (const type of appointmentTypes) {
        try {
          // Check if appointment type exists
          const { data: existingType } = await supabase
            .from('appointment_types')
            .select('id')
            .eq('user_id', userId)
            .eq('external_id', type.Id)
            .maybeSingle();

          let appointmentType;
          if (existingType) {
            // Update existing type
            const { data: updatedType } = await supabase
              .from('appointment_types')
              .update({
                name: type.AppointmentTypeName,
                description: type.Description,
                is_active: type.IsActive,
                category_id: type.CategoryId,
                sync_status: 'synced'
              })
              .eq('id', existingType.id)
              .select()
              .single();
            appointmentType = updatedType;
          } else {
            // Insert new type
            const { data: newType } = await supabase
              .from('appointment_types')
              .insert({
                user_id: userId,
                external_id: type.Id,
                name: type.AppointmentTypeName,
                description: type.Description,
                is_active: type.IsActive,
                category_id: type.CategoryId,
                sync_status: 'synced'
              })
              .select()
              .single();
            appointmentType = newType;
          }

          // Sync subtypes if we have the appointment type and subtypes array
          if (appointmentType && Array.isArray(type.Subtypes)) {
            for (const subtype of type.Subtypes) {
              try {
                // Check if subtype exists
                const { data: existingSubtype } = await supabase
                  .from('appointment_subtypes')
                  .select('id')
                  .eq('appointment_type_id', appointmentType.id)
                  .eq('external_id', subtype.Id)
                  .maybeSingle();

                if (existingSubtype) {
                  // Update existing subtype
                  await supabase
                    .from('appointment_subtypes')
                    .update({
                      name: subtype.Name
                    })
                    .eq('id', existingSubtype.id);
                } else {
                  // Insert new subtype
                  await supabase
                    .from('appointment_subtypes')
                    .insert({
                      appointment_type_id: appointmentType.id,
                      external_id: subtype.Id,
                      name: subtype.Name
                    });
                }
              } catch (error) {
                console.error(`Error syncing subtype for ${type.AppointmentTypeName}:`, error);
                continue;
              }
            }
          }
        } catch (error) {
          console.error(`Error syncing appointment type ${type.AppointmentTypeName}:`, error);
          continue;
        }
      }
    } catch (error) {
      console.error('Error syncing appointment types:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to sync appointment types';
      toast.error(errorMessage, { id: 'sync' });
      throw new Error('Failed to sync appointment types: ' + errorMessage);
    }

    // Sync procedures
    if (onProgress) onProgress('Syncing procedures...', 50);
    toast.loading('Syncing procedures...', { id: 'sync' });
    const proceduresSyncLog = await createSyncLog('procedures', new Date().toISOString());

    try {
      let procedures: Procedure4D[] = [];
      try {
        procedures = await api.getProcedures();
        if (!Array.isArray(procedures)) {
          throw new Error('Invalid procedures response format');
        }
      } catch (error) {
        console.error('Error fetching procedures:', error);
        throw new Error('Failed to fetch procedures from 4D EMR');
      }

      totalCount = procedures.length;
      successCount = 0;
      errorCount = 0;
      const itemResults: Array<{ external_id: number | string; status: string; message?: string }> = [];

      for (let i = 0; i < procedures.length; i++) {
        const proc = procedures[i];
        try {
          // Find matching provider by external_id if available for provider mapping; nullable if not found
          let providerId: string | null = null;
          if (proc.Provider?.Id !== undefined && proc.Provider?.Id !== null) {
            const { data: providerRow } = await supabase
              .from('providers')
              .select('id')
              .eq('user_id', userId)
              .eq('external_id', proc.Provider.Id)
              .maybeSingle();
            providerId = providerRow?.id || null;
          }

          // Upsert procedure
          const { data: upsertedProc, error: upsertError } = await supabase
            .from('procedures')
            .upsert({
              user_id: userId,
              external_id: proc.Id,
              provider_id: providerId,
              name: proc.ProcedureName,
              procedure_fee: proc.ProcedureFee,
              duration_min: proc.DurationMin,
              is_active: proc.IsActive,
              facility_fee: proc.FacilityFee,
              anesthesia_fee: proc.AnesthesiaFee,
              cpt: proc.CPT,
              modifier: proc.Modifier,
              or_procedure: proc.ORProcedure,
              office_service: proc.OfficeService,
              note: proc.Note,
              sync_status: 'received',
              last_sync: new Date().toISOString(),
            }, { onConflict: 'user_id,external_id' })
            .select()
            .single();

          if (upsertError) {
            throw upsertError;
          }

          let procedureId = upsertedProc?.id as string | undefined;
          if (!procedureId) {
            const { data: fetchedProc } = await supabase
              .from('procedures')
              .select('id')
              .eq('user_id', userId)
              .eq('external_id', proc.Id)
              .maybeSingle();
            procedureId = fetchedProc?.id;
          }

          if (!procedureId) {
            throw new Error(`Failed to resolve internal procedure ID for external ${proc.Id}`);
          }

          // Refresh related collections: report tags, ASPS categories, and inventory items
          await supabase.from('procedure_report_tags').delete().eq('procedure_id', procedureId);
          await supabase.from('procedure_asps_categories').delete().eq('procedure_id', procedureId);
          await supabase.from('procedure_inventory_items').delete().eq('procedure_id', procedureId);

          if (proc.ReportTag && typeof proc.ReportTag.Id === 'number') {
            await supabase
              .from('procedure_report_tags')
              .insert({
                procedure_id: procedureId,
                external_id: proc.ReportTag.Id,
                name: proc.ReportTag.Name || 'Report Tag',
              });
          }

          if (Array.isArray(proc.ASPSCategories)) {
            const rows = proc.ASPSCategories.map(cat => ({
              procedure_id: procedureId!,
              external_id: cat.Id,
              name: cat.Name || 'ASPS Category',
            }));
            if (rows.length > 0) {
              await supabase.from('procedure_asps_categories').insert(rows);
            }
          }

          if (Array.isArray(proc.Inventory)) {
            const rows = proc.Inventory.map(item => ({
              procedure_id: procedureId!,
              inventory_id: item.InventoryId,
              inventory_price_option_id: item.InventoryPriceOptionId,
              quantity: item.Quantity,
              title: item.InventoryItemTitle,
            }));
            if (rows.length > 0) {
              await supabase.from('procedure_inventory_items').insert(rows);
            }
          }

          itemResults.push({ external_id: proc.Id, status: 'success' });
          successCount++;

          if ((i + 1) % 50 === 0 || i === procedures.length - 1) {
            updatePhaseProgress('Syncing procedures...', 50, 55, i, procedures.length);
            toast.loading(`Syncing procedures... (${i + 1}/${procedures.length})`, { id: 'sync' });
          }
        } catch (error: any) {
          const msg = error?.message || String(error);
          console.error(`Error syncing procedure ${proc?.Id}:`, error);
          itemResults.push({ external_id: proc?.Id ?? 'unknown', status: 'error', message: msg });
          errorCount++;
        }
      }

      await updateSyncLog(
        proceduresSyncLog.id,
        errorCount === 0 ? 'success' : (successCount > 0 && errorCount < totalCount) ? 'warning' : 'error',
        {
          message: `Processed ${successCount} of ${totalCount} procedures.`,
          successCount,
          errorCount,
          totalCount,
          item_results: itemResults
        }
      );
    } catch (error) {
      console.error('Error syncing procedures:', error);
      if (proceduresSyncLog) {
        await updateSyncLog(
          proceduresSyncLog.id,
          'error',
          { message: 'Failed to fetch or process procedures' },
          error instanceof Error ? error.message : String(error)
        );
      }
      throw error;
    }

    // Sync patients (resumable, batched)
    if (onProgress) onProgress('Syncing patients...', 55);
    toast.loading('Syncing patients...', { id: 'sync' });
    const patientSyncStartTime = new Date().toISOString();
    let patientsSyncLog;

    try {
      // Create a sync log entry for patients
      patientsSyncLog = await createSyncLog('patients', patientSyncStartTime);

      // Load or create cursor for initial full sync resume
      let existingCursor: any = null;
      try {
        const res = await supabase
          .from('sync_cursors')
          .select('id,cursor_skip,total_expected')
          .eq('user_id', userId)
          .eq('type', 'patients')
          .maybeSingle();
        existingCursor = res.data || null;
      } catch {
        existingCursor = null; // Table might not exist yet; fallback below
      }

      const PAGE_SIZE = 1000;
      const BATCH_SIZE = 500;

      // Fallback to current patients count if no cursor found or table missing
      let processedSoFar = existingCursor?.cursor_skip || 0;
      if (!processedSoFar) {
        try {
          const { count } = await supabase
            .from('patients')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', userId);
          processedSoFar = Math.max(processedSoFar, count || 0);
        } catch {}
      }
      let totalExpected = existingCursor?.total_expected || 0;
      let page = Math.floor(processedSoFar / PAGE_SIZE) + 1;
      let offsetInPage = processedSoFar % PAGE_SIZE;
      let hasMore = true;

      // Lightweight remote count check to short-circuit if already up-to-date
      try {
        const probe = await api.getPatients({ page: 1, pageSize: 1 });
        const remoteTotal = (probe as any)?.TotalCount as number | undefined;
        if (typeof remoteTotal === 'number' && remoteTotal >= 0) {
          totalExpected = totalExpected || remoteTotal;
          if (processedSoFar >= remoteTotal) {
            // Update/insert cursor and log up-to-date, then skip heavy paging
            await upsertSyncCursor({ cursor_skip: remoteTotal, total_expected: remoteTotal });

            await updateSyncLog(
              patientsSyncLog.id,
              'success',
              { message: `Patients already up-to-date (${remoteTotal}).`, successCount: 0, errorCount: 0, totalCount: 0 }
            );
            toast.success(`Patients already up-to-date (${remoteTotal})`, { id: 'sync' });
            // proceed to next phases without paging patients again
            hasMore = false;
          }
        }
      } catch {}

      const itemResults: Array<{ external_id: number | string; status: string; message?: string }> = [];
      successCount = 0;
      errorCount = 0;

      while (hasMore) {
        const resp = await api.getPatients({ page, pageSize: PAGE_SIZE });
        const items = Array.isArray((resp as any)?.Items) ? (resp as any).Items as any[] : [];
        if (!totalExpected && typeof (resp as any)?.TotalCount === 'number') {
          totalExpected = (resp as any).TotalCount as number;
          // store expected on cursor (update then insert to avoid on_conflict requirement)
          await upsertSyncCursor({ cursor_skip: processedSoFar, total_expected: totalExpected });
        }

        if (items.length === 0) {
          hasMore = false;
          break;
        }

        const startIndex = Math.min(offsetInPage, items.length);
        const pageSlice = items.slice(startIndex);
        offsetInPage = 0; // only applies to the first page after resume

        // Process in batches
        for (let start = 0; start < pageSlice.length; start += BATCH_SIZE) {
          const batch = pageSlice.slice(start, start + BATCH_SIZE);

          // Build patient external id list
          const externalIds = batch
            .map(p => safeExternalId(p.PatientId))
            .filter((v: string) => v !== '');

          // Bulk mapping fetch
          const { data: existingMappings } = await supabase
            .from('external_id_mapping')
            .select('external_id, internal_id')
            .eq('user_id', userId)
            .eq('entity_type', 'patient')
            .in('external_id', externalIds);
          const mapByExternal = new Map<string, string>();
          (existingMappings || []).forEach((m: any) => mapByExternal.set(m.external_id, m.internal_id));

          // Insert any missing mappings in bulk
          const missing = externalIds.filter(eid => !mapByExternal.has(eid));
          if (missing.length > 0) {
            const rows = missing.map(eid => ({ user_id: userId, external_id: eid, internal_id: generateUUID(), entity_type: 'patient' }));
            const { data: created } = await supabase
              .from('external_id_mapping')
              .insert(rows)
              .select('external_id, internal_id');
            (created || []).forEach((m: any) => mapByExternal.set(m.external_id, m.internal_id));
          }

          // Build upsert rows
          const upsertRows = batch.map((patient: any) => {
            const externalId = safeExternalId(patient.PatientId);
            const id = mapByExternal.get(externalId);
            const createdAt = safeToISOString(patient.CreatedDate);
            const updatedAt = safeToISOString(patient.ModifiedDate);
            return {
              id,
              user_id: userId,
              external_id: externalId,
              first_name: patient.FirstName,
              last_name: patient.LastName,
              gender: patient.Gender,
              dob: patient.DOB,
              address1: patient.Address1,
              address2: patient.Address2,
              city: patient.City,
              state: patient.State,
              zipcode: patient.ZipCode,
              country: patient.Country,
              phone_primary: patient.PhonePrimary,
              email: patient.Email,
              referral_category: patient.ReferralCategory,
              marketing_source: patient.MarketingSource,
              status: patient.Status,
              nickname: patient.NickName,
              other_last_name: patient.OtherLastName,
              middle_initial: patient.MiddleInitial,
              custom1: patient.Custom1,
              custom2: patient.Custom2,
              custom3: patient.Custom3,
              profile_photo_id: patient.ProfilePhotoId,
              coordinator_id: patient.CoordinatorId,
              account_number: patient.AccountNumber,
              external_created_at: createdAt,
              external_updated_at: updatedAt,
              zonedm_sync_status: 'received',
            };
          });

          if (upsertRows.length > 0) {
            const { error: upsertErr } = await supabase
              .from('patients')
              .upsert(upsertRows); // primary key id ensures idempotency
            if (upsertErr) {
              upsertRows.forEach(r => itemResults.push({ external_id: r.external_id, status: 'error', message: upsertErr.message || String(upsertErr) }));
              errorCount += upsertRows.length;
            } else {
              upsertRows.forEach(r => itemResults.push({ external_id: r.external_id, status: 'success' }));
              successCount += upsertRows.length;
            }
          }

          processedSoFar += batch.length;
          // Update cursor after each batch (update then insert)
          await upsertSyncCursor({ cursor_skip: processedSoFar, total_expected: totalExpected });

          // Progress update
          const denom = totalExpected || (processedSoFar + (PAGE_SIZE - (processedSoFar % PAGE_SIZE)));
          const pct = Math.floor((processedSoFar / Math.max(denom, 1)) * 100);
          if (onProgress) onProgress(`Syncing patients... (${processedSoFar}/${totalExpected || '?'})`, Math.min(70, 55 + Math.floor(pct * 0.15)));
          toast.loading(`Syncing patients... (${processedSoFar}/${totalExpected || '?'})`, { id: 'sync' });
        }

        // Advance to next page
        page++;
        if (items.length < PAGE_SIZE) {
          hasMore = false;
        }
      }

      totalCount = processedSoFar;

      // Mark cursor complete (update then insert)
      await upsertSyncCursor({ cursor_skip: 0, total_expected: null });

      // Update patients sync log
      await updateSyncLog(
        patientsSyncLog.id,
        errorCount === 0 ? 'success' : (successCount > 0 && errorCount < totalCount) ? 'warning' : 'error',
        { 
          message: `Processed ${successCount} patients (errors: ${errorCount}).`,
          successCount,
          errorCount,
          totalCount,
          item_results: itemResults
        }
      );
    } catch (error: any) {
      console.error('Error syncing patients:', error);
      if (patientsSyncLog) {
        await updateSyncLog(
          patientsSyncLog.id,
          'error',
          {
            message: 'Failed to fetch or process patients',
            errorCount: 0,
            successCount: 0,
            totalCount: 0,
            item_results: [
              { status: 'error', message: error?.message || String(error) }
            ]
          },
          error?.message || String(error)
        );
      }
      toast.error('Failed to sync patients.');
    } finally {
      toast.dismiss('sync');
    }

    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);

    // Sync patient charges for the last 30 days
    if (onProgress) onProgress('Syncing patient charges...', 70);
    toast.loading('Syncing patient charges...', { id: 'sync' });
    const chargesSyncLog = await createSyncLog('charges', new Date().toISOString());

    try {
      const beginIso = startDate.toISOString().split('T')[0];
      const endIsoStr = new Date().toISOString().split('T')[0];
      const chargesResponse = await api.getPatientCharges({
        beginDate: beginIso,
        endDate: endIsoStr
      });

      if (!Array.isArray(chargesResponse)) {
        throw new Error('Invalid patient charges response format');
      }

      // Fast path: skip already-synced charges by invoice_id in the same date range
      let existingInvoices: Array<{ invoice_id: string | number }>|null = null;
      try {
        const { data } = await supabase
          .from('patient_charges')
          .select('invoice_id')
          .eq('user_id', userId)
          .gte('invoice_date', beginIso)
          .lte('invoice_date', endIsoStr);
        existingInvoices = data as any;
      } catch {}

      const existingSet = new Set((existingInvoices || []).map(r => String((r as any).invoice_id)));
      const toProcess = chargesResponse.filter(c => !existingSet.has(String(c.InvoiceId)));

      totalCount = toProcess.length;
      successCount = 0;
      errorCount = 0;
      const itemResults = [] as Array<{ external_id: number | string; status: string; message?: string }>

      if (toProcess.length === 0) {
        // Nothing new to sync
        await updateSyncLog(
          chargesSyncLog.id,
          'success',
          {
            message: `Patient charges already up-to-date (${existingSet.size})`,
            totalCount: 0,
            successCount: 0,
            errorCount: 0,
            startDate: beginIso,
            endDate: endIsoStr,
            item_results: []
          }
        );
      } else {
        // Process and insert in batches
        const BATCH_SIZE = 500;
        for (let i = 0; i < toProcess.length; i += BATCH_SIZE) {
          const batch = toProcess.slice(i, i + BATCH_SIZE);

          const rows = [] as any[];
          for (const charge of batch) {
            try {
              const { data: patientMapping } = await supabase
                .from('external_id_mapping')
                .select('internal_id')
                .eq('user_id', userId)
                .eq('external_id', safeExternalId(charge.PatientID))
                .eq('entity_type', 'patient')
                .maybeSingle();

              const { data: providerMapping } = await supabase
                .from('external_id_mapping')
                .select('internal_id')
                .eq('user_id', userId)
                .eq('external_id', safeExternalId(charge.ProviderID))
                .eq('entity_type', 'provider')
                .maybeSingle();

              rows.push({
                user_id: userId,
                patient_id: patientMapping?.internal_id,
                provider_id: providerMapping?.internal_id,
                external_patient_id: charge.PatientID,
                patient_dob: charge.PatientDOB,
                gender: charge.Gender,
                referral_category: charge.ReferralCategory,
                referral_subcategory: charge.ReferralSubcategory,
                facility_name: charge.FacilityName,
                invoice_id: charge.InvoiceId,
                invoice_date: charge.InvoiceDate,
                item_description: charge.ItemDescription,
                item_category: charge.ItemCategory,
                unit_price: charge.UnitPrice,
                unit_discount: charge.UnitDiscount,
                quantity: charge.Quantity,
                lead_id: charge.LeadId,
                lead_custom_data: charge.LeadCustomData ? JSON.parse(charge.LeadCustomData) : null,
                sync_status: 'synced',
                last_sync: new Date().toISOString()
              });
              itemResults.push({ external_id: charge.PatientID, status: 'success' });
              successCount++;
            } catch (e: any) {
              itemResults.push({ external_id: String(charge?.PatientID ?? ''), status: 'error', message: e?.message || String(e) });
              errorCount++;
            }
          }

          if (rows.length > 0) {
            const { error } = await supabase.from('patient_charges').insert(rows);
            if (error) {
              // Fall back to per-row insert to avoid entire batch failing
              for (const r of rows) {
                try {
                  const { error: insErr } = await supabase.from('patient_charges').insert(r);
                  if (insErr) throw insErr;
                } catch (ie) {
                  // mark as error in counts if needed
                }
              }
            }
          }

          const processed = Math.min(i + BATCH_SIZE, toProcess.length) - 1;
          updatePhaseProgress('Syncing patient charges...', 70, 85, processed, toProcess.length);
          toast.loading(`Syncing patient charges... (${Math.min(i + BATCH_SIZE, toProcess.length)}/${toProcess.length})`, { id: 'sync' });
        }
      }

      // Update charges sync log
      await updateSyncLog(
        chargesSyncLog.id,
        errorCount === 0 ? 'success' : (successCount > 0 && errorCount < totalCount) ? 'warning' : 'error',
        {
          totalCount,
          successCount,
          errorCount,
          startDate: startDate.toISOString(),
          endDate: new Date().toISOString(),
          item_results: itemResults,
        }
      );

    } catch (error) {
      console.error('Error syncing patient charges:', error);
      if (chargesSyncLog) {
        await updateSyncLog(
          chargesSyncLog.id,
          'error',
          {
            totalCount,
            successCount,
            errorCount: totalCount - successCount,
            item_results: [],
          },
          error instanceof Error ? error.message : String(error)
        );
      }
      throw error;
    }

    // Appointments: for fullHistoricalSync, pull ALL appointments. Otherwise last 30 days
    if (onProgress) onProgress(options?.fullHistoricalSync ? 'Syncing appointments...' : 'Syncing recent appointments...', 85);
    toast.loading(options?.fullHistoricalSync ? 'Syncing appointments...' : 'Syncing recent appointments...', { id: 'sync-appointments' });
    const syncStartTime = new Date().toISOString();
    let appointmentsSyncLog;

    try {
      // Create a sync log entry for appointments
      appointmentsSyncLog = await createSyncLog('appointments', new Date().toISOString());

      // Determine date range based on sync type
      const endIso = new Date().toISOString();
      let appointmentList: any[] = [];
      if (options?.fullHistoricalSync) {
        // Pull ALL appointments with pagination (999/page)
        appointmentList = await api.getAllAppointments({});
      } else {
        // Last 30 days
        const startDt = new Date();
        startDt.setDate(startDt.getDate() - 30);
        appointmentList = await api.getAllAppointments({ fromDate: startDt.toISOString(), toDate: endIso });
      }
      
      // Clear any existing appointment sync logs to prevent duplicates
      await supabase
        .from('sync_logs')
        .delete()
        .eq('user_id', userId)
        .eq('type', 'appointments')
        .eq('start_time', syncStartTime);

      totalCount = appointmentList.length;
      successCount = 0;
      errorCount = 0;
      const itemResults = [] as Array<{ external_id: number | string; status: string; message?: string }>;

      // Cache all appointment type IDs for this user to avoid per-row lookups
      const { data: allApptTypes } = await supabase
        .from('appointment_types')
        .select('id, external_id')
        .eq('user_id', userId);
      const apptTypeIdByExternal = new Map<number, string>();
      (allApptTypes || []).forEach((t: { id: string; external_id: number | null }) => {
        if (typeof t.external_id === 'number' && t.id) {
          apptTypeIdByExternal.set(t.external_id, t.id);
        }
      });

      // Cache appointment subtype IDs keyed by (type_id, external_id)
      const typeIds = Array.from(apptTypeIdByExternal.values());
      const subtypeIdByTypeExternal = new Map<string, string>();
      if (typeIds.length > 0) {
        const { data: allSubtypes } = await supabase
          .from('appointment_subtypes')
          .select('id, appointment_type_id, external_id')
          .in('appointment_type_id', typeIds);
        (allSubtypes || []).forEach((st: any) => {
          const key = `${st.appointment_type_id}:${st.external_id}`;
          if (st.id) subtypeIdByTypeExternal.set(key, st.id as string);
        });
      }

      const BATCH_SIZE = 500;
      // Preload existing appointments set for fast skip on fullHistorical
      let existingApptIds = new Set<string>();
      if (options?.fullHistoricalSync && totalCount > 0) {
        try {
          console.log('Loading existing appointment IDs to skip duplicates...');
          let offset = 0;
          const pageSize = 10000;
          let hasMore = true;
          
          while (hasMore) {
            const { data, error } = await supabase
              .from('appointments')
              .select('external_id')
              .eq('user_id', userId)
              .range(offset, offset + pageSize - 1);
            
            if (error) {
              console.error('Error loading existing appointment IDs:', error);
              break;
            }
            
            if (data && data.length > 0) {
              data.forEach((r: any) => existingApptIds.add(String(r.external_id)));
              console.log(`Loaded ${data.length} existing appointment IDs (total: ${existingApptIds.size})`);
              offset += data.length;
              hasMore = data.length === pageSize;
            } else {
              hasMore = false;
            }
          }
          
          console.log(`Total existing appointments in DB: ${existingApptIds.size}`);
        } catch (err) {
          console.error('Failed to preload existing appointment IDs:', err);
        }
      }
      for (let startIndex = 0; startIndex < appointmentList.length; startIndex += BATCH_SIZE) {
        const batch = appointmentList.slice(startIndex, startIndex + BATCH_SIZE);

        // Prepare patient mappings for this batch in bulk
        const uniquePatientIds = Array.from(new Set(
          batch
            .map(a => a.PatientId)
            .filter((v): v is number => typeof v === 'number')
            .map(id => safeExternalId(id))
        ));

        const patientUuidByExternal = new Map<string, string>();
        if (uniquePatientIds.length > 0) {
          const { data: existingMappings } = await supabase
            .from('external_id_mapping')
            .select('external_id, internal_id')
            .eq('user_id', userId)
            .eq('entity_type', 'patient')
            .in('external_id', uniquePatientIds);
          (existingMappings || []).forEach((m: any) => patientUuidByExternal.set(m.external_id, m.internal_id));

          // Insert any missing mappings in one call
          const missing = uniquePatientIds.filter(eid => !patientUuidByExternal.has(eid));
          if (missing.length > 0) {
            const rows = missing.map(eid => ({
              user_id: userId,
              external_id: eid,
              internal_id: generateUUID(),
              entity_type: 'patient'
            }));
            const { data: created } = await supabase
              .from('external_id_mapping')
              .insert(rows)
              .select('external_id, internal_id');
            (created || []).forEach((m: any) => patientUuidByExternal.set(m.external_id, m.internal_id));
          }
        }

        // Prepare staff IDs for the batch in bulk
        const uniqueStaffIds = Array.from(new Set(
          batch
            .map(a => a.StaffId)
            .filter((v): v is number => typeof v === 'number')
        ));
        const staffUuidByExternal = new Map<number, string>();
        if (uniqueStaffIds.length > 0) {
          const { data: staffRows } = await supabase
            .from('staff')
            .select('id, external_id')
            .eq('user_id', userId)
            .in('external_id', uniqueStaffIds);
          (staffRows || []).forEach((s: any) => staffUuidByExternal.set(s.external_id as number, s.id as string));
        }

        // Build appointment rows for upsert
        const upsertRows: any[] = [];
        for (const appointment of batch) {
          if (options?.fullHistoricalSync && existingApptIds.has(String(appointment.Id))) {
            // Already present; skip building row
            itemResults.push({ external_id: appointment.Id, status: 'success' });
            successCount++;
            continue;
          }
          try {
            const mappedStatus = mapAppointmentStatus(appointment.Status);

            const typeId = apptTypeIdByExternal.get(appointment.TypeId);
            if (!typeId) {
              throw new Error(`Could not get UUID for appointment type: ${appointment.TypeId}`);
            }

            const patientUuid = appointment.PatientId
              ? patientUuidByExternal.get(safeExternalId(appointment.PatientId)) || null
              : null;
            const staffUuid = appointment.StaffId
              ? (staffUuidByExternal.get(appointment.StaffId) || null)
              : null;
            const subtypeUuid = (appointment.SubtypeId && typeId)
              ? (subtypeIdByTypeExternal.get(`${typeId}:${appointment.SubtypeId}`) || null)
              : null;

            const startTime = safeToISOString(appointment.Start);
            let endTime = safeToISOString(appointment.End);
            if (startTime && endTime && new Date(endTime) <= new Date(startTime)) {
              const newEndTime = new Date(startTime);
              newEndTime.setMinutes(newEndTime.getMinutes() + 30);
              endTime = newEndTime.toISOString();
            }

            upsertRows.push({
              user_id: userId,
              external_id: appointment.Id,
              type_id: typeId,
              subtype_id: subtypeUuid,
              staff_id: staffUuid,
              patient_id: patientUuid,
              facility_location_id: appointment.FacilityLocationId,
              all_day: appointment.AllDay,
              start_time: startTime,
              end_time: endTime,
              subject: appointment.Subject,
              comment: appointment.Comment,
              cancel_reason: appointment.CancelReason,
              consult_reason: appointment.ConsultReason,
              move_up_list: appointment.MoveUpList,
              block_notifications: appointment.BlockNotifications,
              status: mappedStatus,
              checkin_time: appointment.CheckinTime,
              checkout_time: appointment.CheckoutTime,
              roomed_time: appointment.RoomedTime,
              chart_note_needed: appointment.ChartNoteNeeded,
              external_created_at: appointment.CreatedDate,
              external_updated_at: appointment.ModifiedDate,
              zonedm_sync_status: 'received'
            });
          } catch (err: any) {
            itemResults.push({ external_id: appointment.Id, status: 'error', message: err?.message || String(err) });
            errorCount++;
          }
        }

        if (upsertRows.length > 0) {
          const { error: upsertErr } = await supabase
            .from('appointments')
            .upsert(upsertRows, { onConflict: 'user_id,external_id' });
          if (upsertErr) {
            // Fallback to per-row update then insert (to avoid upsert 400s)
            for (const r of upsertRows) {
              try {
                const { data: upd, error: updErr } = await supabase
                  .from('appointments')
                  .update(r)
                  .eq('user_id', userId)
                  .eq('external_id', r.external_id)
                  .select('id');
                if (updErr || !upd || upd.length === 0) {
                  const { error: insErr } = await supabase
                    .from('appointments')
                    .insert(r);
                  if (insErr) throw insErr;
                }
                itemResults.push({ external_id: r.external_id, status: 'success' });
                successCount++;
              } catch (e: any) {
                itemResults.push({ external_id: r.external_id, status: 'error', message: e?.message || String(e) });
                errorCount++;
              }
            }
          } else {
            for (const r of upsertRows) {
              itemResults.push({ external_id: r.external_id, status: 'success' });
            }
            successCount += upsertRows.length;
          }
        }

        // Progress update after each batch
        const processed = Math.min(startIndex + BATCH_SIZE, appointmentList.length) - 1;
        const label = options?.fullHistoricalSync ? 'Syncing appointments...' : 'Syncing recent appointments...';
        updatePhaseProgress(label, 85, 98, processed, appointmentList.length);
        toast.loading(`${label} (${Math.min(startIndex + BATCH_SIZE, appointmentList.length)}/${appointmentList.length})`, { id: 'sync-appointments' });
      }

      // Update appointments sync log
      await updateSyncLog(
        appointmentsSyncLog.id,
        errorCount === 0 ? 'success' : (successCount > 0 && errorCount < totalCount) ? 'warning' : 'error',
        {
          totalCount,
          successCount,
          errorCount,
          startDate: startDate.toISOString(),
          endDate: new Date().toISOString(),
          item_results: itemResults,
        }
      );

    } catch (error) {
      console.error('Error syncing appointments:', error);
      if (appointmentsSyncLog) {
        await updateSyncLog(
          appointmentsSyncLog.id,
          'error',
          {
            totalCount,
            successCount,
            errorCount: totalCount - successCount,
            item_results: [],
          },
          error instanceof Error ? error.message : String(error)
        );
      }
      throw error;
    }

    clearAllSyncToasts();
    toast.success('Initial data sync completed successfully!');
    await updateIntegrationLastSync();
    if (onProgress) onProgress('Sync complete!', 100);
    if (onComplete) onComplete({ success: true });
    return true;
  } catch (error) {
    clearAllSyncToasts();
    toast.error('Error syncing data. Please try again.');
    if (onProgress) onProgress('Sync failed', 100);
    if (onComplete) onComplete({ success: false, error });
    if (error instanceof Error) {
      console.error('Error syncing initial data:', error.message);
    } else {
      console.error('Error syncing initial data:', error);
    }
    throw error;
  }
}

async function getAppointmentTypeId(userId: string, externalTypeId: number) {
  // First check if we have a mapping
  const { data: mapping } = await supabase
    .from('appointment_type_mapping')
    .select('internal_id')
    .eq('user_id', userId)
    .eq('external_id', externalTypeId)
    .single();

  if (mapping?.internal_id) {
    return mapping.internal_id;
  }

  // If no mapping exists, get the appointment type and create mapping
  const { data: appointmentType } = await supabase
    .from('appointment_types')
    .select('id')
    .eq('user_id', userId)
    .eq('external_id', externalTypeId)
    .single();

  if (!appointmentType?.id) {
    throw new Error(`Appointment type not found for external ID: ${externalTypeId}`);
  }

  // Create mapping
  const { data: newMapping } = await supabase
    .from('appointment_type_mapping')
    .insert({
      user_id: userId,
      external_id: externalTypeId,
      internal_id: appointmentType.id
    })
    .select('internal_id')
    .single();

  if (!newMapping) {
    throw new Error('Failed to create appointment type mapping');
  }

  return newMapping.internal_id;
}

export { syncInitialData };